<template>
  <a
    v-if="type == 'link' && !url"
    href="#"
    class="t-link"
    @click="onClick"
  >
    <slot v-if="hasSlot" />
    <template v-else>
      {{ text }}
    </template>
  </a>
  <nuxt-link
    v-else-if="type == 'link' && !externalUrl"
    :to="url"
    class="t-link"
    @click.native="onClick"
  >
    <slot v-if="hasSlot" />
    <template v-else>
      {{ text }}
    </template>
  </nuxt-link>
  <a
    v-else-if="type == 'link' && externalUrl"
    :href="url"
    class="t-link"
    @click="onClick"
  >
    <slot v-if="hasSlot" />
    <template v-else>
      {{ text }}
    </template>
  </a>
  <nuxt-link
    v-else-if="url && !externalUrl"
    :class="buttonClass"
    :to="url"
    :disabled="isDisabled"
    :style="customColor ? {backgroundColor:customColor, border:'none'} : {}"
    @click.native="onClick"
  >
    <slot v-if="hasSlot" />
    <template v-else>
      {{ text }}
    </template>
  </nuxt-link>
  <a
    v-else-if="url && externalUrl"
    :class="buttonClass"
    :href="url"
    :disabled="isDisabled"
    :style="customColor ? {backgroundColor:customColor, border:'none'} : {}"
    @click="onClick"
  >
    <slot v-if="hasSlot" />
    <template v-else>
      {{ text }}
    </template>
  </a>
  <button
    v-else-if="text || hasSlot"
    :class="buttonClass"
    :disabled="isDisabled"
    :style="customColor ? {backgroundColor:customColor, border:'none'} : {}"
    @click="onClick"
  >
    <slot v-if="hasSlot" />
    <template v-else>
      {{ text }}
    </template>
  </button>
</template>

<script>
export default {
  name: "Btn",
  props: {
    buttonSize: {
      type: [String, Array],
      default: null,
    },
    text: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    url: {
      type: String,
      default: null,
    },
    externalUrl: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    customColor: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      link: false,
    };
  },
  computed: {
    buttonClass() {
      let classModifiers = "";
      if (this.isDisabled) {
        classModifiers += "c-btn--disabled c-btn--"  + this.type + "--disabled " ;
      }
      if (this.type) {
        classModifiers += "c-btn--" + this.type;
      } else {
        classModifiers += "c-btn--primary";
      }
      if (this.buttonSize) {
        if (Array.isArray(this.buttonSize)) {
          for(let size of this.buttonSize) {
            classModifiers += " c-btn--" + size;
          }
        } else {
          classModifiers += " c-btn--" + this.buttonSize;
        }

      }

      return "c-btn " + classModifiers;
    },
    hasSlot() {
      return !!this.$slots.default;
    },
  },
  created() {
    if (!this.hasSlot && !this.text) {
      console.warn("Your button/CTA has no text!");
    }
  },
  methods: {
    onClick(event) {
      this.$emit('click', event);
    },
  },
};
</script>

<style lang="scss">
// NOTE: please keep these styles un-scoped
//  so that they can be shared with elements
//  injected by third-party JavaScript.
.c-btn {
  font-size: rem(15px);
  line-height: rem(20px);
  letter-spacing: rem(1px);
  text-decoration: none;
  font-weight: $font-weight-demi;
  &,
  &:link,
  &:visited {
    width: rem(300px);
    max-width: 100%;
    padding: rem(15px) rem(30px);
    display: inline-block;
    text-align: center;
    text-transform: uppercase;
    border: solid $border-width;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    position: relative;
    backface-visibility: hidden;

    @include respond(tab-land) {
      width: auto;
    }

    &:hover {
      transform: translateY(3px);
      box-shadow: 0 1rem 2rem rgba(#000, 0.2);

      &::after {
        transform: scaleX(1.4) scaleY(1.6);
        opacity: 0;
      }
    }
    &:after {
      content: "";
      display: inline-block;
      height: 100%;
      width: 100%;
      border-radius: 10rem;
      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;
      transition: all 0.4s ease-in-out;
    }

    &:active,
    &:focus {
      outline: none;
      transform: translateY(-1px);
      box-shadow: 0 0.5rem 1rem rgba(#000, 0.2);
    }

    @include respond(phone) {
      width: auto;
    }
  }

  &--large {
    &,
    &:link,
    &:visited {
      width: 100%;
    }
  }

  &--small {
    &,
    &:link,
    &:visited {
      width: rem(210px);
      max-width: 100%;
    }
  }

  &--x-small {
    &,
    &:link,
    &:visited {
      width: auto;
      max-width: 100%;
    }
  }

  &--thin {
    &,
    &:link,
    &:visited {
      padding: rem(8px) rem(30px);
    }
  }

  &--disabled {
    &,
    &:link,
    &:visited {
      cursor: no-drop;
      pointer-events: none;
      @include themify($themes) {
        color: themed('foreground', 'white-text');
        background-color: themed('background', 'white');
        border:0px;
      }
    }
  }

  &--primary {
    &,
    &:link,
    &:visited {
      @include themify($themes) {
        border-color: themed("primary");
        background-color: themed("primary");
        color: themed("foreground", "white-text");
      }
    }
    &:hover {
      @include themify($themes) {
        border-color: themed("primary", "hover");
        background-color: themed("primary", "hover");
      }
    }

    &:active,
    &:focus {
      @include themify($themes) {
        border-color: themed("primary", "focus");
        background-color: themed("primary", "focus");
      }
    }
  }

  &--secondary {
    &,
    &:link,
    &:visited {
      @include themify($themes) {
        border-color: themed("foreground", 'white-text');
        background-color: transparent;
        color: themed("foreground", "white-text");
      }
    }

    &:hover {
      @include themify($themes) {
        background-color: themed("foreground", "white-text");
        color: themed("foreground");
      }

      &:active,
      &:focus {
        @include themify($themes) {
          background-color: themed("foreground", "white-focus");
          color: themed("foreground", 'base');
        }
      }
    }
  }
  &--secondary-white {
    &,
    &:link,
    &:visited {
      @include themify($themes) {
        border-color: themed("foreground", 'white-text');
        background-color: transparent;
        color: themed("foreground", "white-text");
      }
    }

    &:hover {
      @include themify($themes) {
        background-color: themed("foreground", "base");
        color: themed("foreground", "text");
      }

      &:active,
      &:focus {
        @include themify($themes) {
          background-color: themed("foreground", "white-focus");
          color: themed("foreground", 'white-focus');
        }
      }
    }
  }
  &--primary-open {
    &,
    &:link,
    &:visited {
      @include themify($themes) {
        background-color:themed('foreground', 'white-text');
        color: themed('foreground');
        border:2px solid themed('foreground');
      }
    }
    &:hover {
      @include themify($themes) {
        color: themed('foreground');
        border:2px solid themed('foreground');
        background-color: themed('foreground', 'white');
      }
    }
    &:active,
    &:focus {
      @include themify($themes) {
        background-color: themed('foreground', 'white');
        color: themed('foreground');
      }
    }
    &--disabled {
      &,
      &:link,
      &:visited {
        cursor: no-drop;
        pointer-events: none;
        @include themify($themes) {
          color: themed('foreground', 'foreground');
          background-color: themed('background', 'white');
          border:2px themed('foreground', 'foreground') solid;
        }
      }
    }
  }
  &--secondary-dark {
    &,
    &:link,
    &:visited {
      @include themify($themes) {
        background-color:rgba(0, 0, 0, 0);
        //background-color:themed('foreground', 'white-text');
        color: themed('foreground');
        border:2px solid themed('foreground');
      }
    }
    &:hover {
      @include themify($themes) {
        color: themed('foreground', 'white-text');
        border-color: themed('foreground', 'base');
        background-color: themed('foreground', 'base');
      }
    }
    &:active,
    &:focus {
      @include themify($themes) {
        background-color: themed('foreground', 'white');
        color: themed('foreground');
      }
    }
  }
}
</style>
